// frontend/app/components/lessonPlans/present/presentTheme.js
//
// The presentation type scale, and the colours that survive a projector.
//
// Present mode is read from the back row, not from a laptop at arm's length.
// None of the workspace's 11–14px sizes make that trip, and none of them is the
// starting point here: multiplying a reading scale gives you a reading scale
// with big numbers, where the caption still competes with the question. This is
// a scale designed for the room — five sizes, a deliberate gap between each,
// and one of them (chrome) that pointedly does not grow at all.
//
// React Native has no clamp() and no vw, so the CSS idea is done in arithmetic:
// every size ramps linearly between two viewport widths and is flat outside
// them. PRESENT_BASIS_MIN is the smallest projector we design for and its sizes
// are the legibility floor; PRESENT_BASIS_MAX is what a 1080p screen can afford
// before a question stops fitting on one screen.

import { getLessonColors } from "../lessonPlanTheme";

/** Where the ramp starts — a 1024x768 classroom projector. Sizes are flat below. */
export const PRESENT_BASIS_MIN = 1024;
/** Where the ramp tops out. Sizes are flat above, so a 4K TV is not shouted at. */
export const PRESENT_BASIS_MAX = 1920;

/**
 * min = the size at a 1024-wide projector, max = the size at 1080p and beyond.
 * `lineHeight` is a multiplier: the big sizes get tighter leading because a
 * 40px line with 1.5 leading wastes a third of the wall.
 */
export const PRESENT_TYPE_SCALE = {
  // The question the room is answering is the largest thing on screen —
  // larger than the step title, because the title is context and the
  // question is the work.
  prompt: { min: 32, max: 40, lineHeight: 1.3 },
  stepTitle: { min: 28, max: 36, lineHeight: 1.25 },
  option: { min: 22, max: 28, lineHeight: 1.4 },
  body: { min: 20, max: 24, lineHeight: 1.5 },
  caption: { min: 16, max: 19, lineHeight: 1.45 },
  eyebrow: { min: 13, max: 15, lineHeight: 1.3 },
  // Chrome is the one thing that deliberately stays small. A step counter and a
  // phase name are for the teacher's glance; sized for the back row they would
  // compete with the lesson for the room's attention.
  chrome: { min: 13, max: 15, lineHeight: 1.3 },
};

/**
 * The single number the scale ramps on.
 *
 * Width alone is the wrong driver: a viewport can be wide and short — a 21:9
 * window, or a 16:9 screen with browser chrome eating the top — and vertical
 * room is what actually decides whether a 40px question fits above its options.
 * So the height gets a veto, expressed as the width a 16:9 screen of that
 * height would have.
 */
export const presentBasis = (width, height) => {
  const w = Number(width) > 0 ? Number(width) : PRESENT_BASIS_MIN;
  const h = Number(height) > 0 ? Number(height) : Math.round(w * 0.5625);
  return Math.min(w, h * 1.6);
};

/** One token's size at one basis. Flat outside [MIN, MAX], linear between. */
export const presentFontSize = (token, basis) => {
  const spec = PRESENT_TYPE_SCALE[token];
  if (!spec) return PRESENT_TYPE_SCALE.body.min;
  const span = PRESENT_BASIS_MAX - PRESENT_BASIS_MIN;
  const ratio = Math.max(0, Math.min(1, (basis - PRESENT_BASIS_MIN) / span));
  return Math.round(spec.min + (spec.max - spec.min) * ratio);
};

/**
 * The whole scale for one viewport, as ready-to-spread text styles:
 * `style={[{ ...type.prompt }]}`. Carries the viewport with it so a component
 * that needs to size a surface off the screen (the asset stage) does not have
 * to call useWindowDimensions again and disagree about the answer.
 */
export const getPresentType = (width, height) => {
  const basis = presentBasis(width, height);
  const type = {
    basis,
    viewport: { width: Number(width) || 0, height: Number(height) || 0 },
    // A 3D scene is the centrepiece on a projector, so its surface is sized off
    // the screen rather than off its own contents — it should read as the slide,
    // not as a card sitting on one.
    assetMinHeight: Math.max(260, Math.round((Number(height) || 720) * 0.4)),
  };
  Object.keys(PRESENT_TYPE_SCALE).forEach((token) => {
    const fontSize = presentFontSize(token, basis);
    type[token] = {
      fontSize,
      lineHeight: Math.round(fontSize * PRESENT_TYPE_SCALE[token].lineHeight),
    };
  });
  return type;
};

/**
 * Lesson colours, re-tuned for a wall.
 *
 * A projector crushes mid-tones and lifts blacks: the workspace's textMuted
 * (#64748b) is a readable grey on a monitor and a smear from the back row. Every
 * text tone here is pulled toward its extreme, and the dark background goes
 * deeper than the workspace's #0f172a because a projector's "black" is already
 * grey before the room's lights are counted.
 */
export const getPresentColors = (isDarkMode) => {
  const base = getLessonColors(isDarkMode);
  return {
    ...base,
    pageBg: isDarkMode ? "#0b1220" : "#ffffff",
    panelBg: isDarkMode ? "#111c31" : "#f8fafc",
    subtleBg: isDarkMode ? "rgba(148,163,184,0.10)" : "#f1f5f9",
    textPrimary: isDarkMode ? "#f8fafc" : "#0f172a",
    textSecondary: isDarkMode ? "#cbd5e1" : "#334155",
    textMuted: isDarkMode ? "#94a3b8" : "#475569",
    border: isDarkMode ? "rgba(148,163,184,0.28)" : "#cbd5e1",
    borderStrong: isDarkMode ? "rgba(148,163,184,0.45)" : "#94a3b8",
  };
};
